import { useMutation, useQuery } from '@tanstack/react-query'
import {
	Button,
	Checkbox,
	EmptyState,
	Modal,
	PdfIcon,
	Search,
	SearchIcon,
	Select,
	Spinner,
	Text,
	ZipIcon,
} from '@viapeople/components'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatStepName } from '../../utils/helpers'
import { useToast } from '../../utils/ToastContext'
import { trpc, trpcClient } from '../../utils/trpc'

/**
 * Trigger a download from a presigned S3 URL.
 *
 * We deliberately do NOT fetch+blob the URL: the S3 dev bucket does not always
 * allow CORS for the app origin, and recent AWS SDK versions add headers
 * (x-amz-checksum-mode) that force a preflight, which then fails. Navigating
 * via a hidden <a> sidesteps this — the presigned URL has
 * response-content-disposition=attachment, so S3 sends the download header and
 * the browser saves the file without JS reading the body. The cross-origin
 * `download` attribute is ignored; the saved name comes from the URL path /
 * S3's content-disposition (which encodes the server-built filename).
 */
const downloadFile = (url: string, _filename: string) => {
	const link = document.createElement('a')
	link.href = url
	link.rel = 'noopener'
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

type PdfPerspective = 'self' | 'manager' | 'ca'

const PERSPECTIVE_LABELS: Record<PdfPerspective, string> = {
	self: 'Self',
	manager: 'Manager',
	ca: 'CA',
}

interface PerspectiveButtonsProps {
	userId: string
	loadingKeys: Set<string>
	onDownload: (userId: string, perspective: PdfPerspective) => void
	/**
	 * When false, the Manager button is disabled with an explanatory tooltip — the backend
	 * would otherwise throw `NoPrimaryManagerAssigned` after the click. Reviewees who are
	 * not in the org hierarchy (e.g. external interns) typically land here.
	 */
	hasManager: boolean
}

/** Three-button row (Self / Manager / CA) used for each participant. */
const PerspectiveButtons: React.FC<PerspectiveButtonsProps> = ({ userId, loadingKeys, onDownload, hasManager }) => (
	<div className='flex items-center gap-1.5 flex-shrink-0'>
		{(['self', 'manager', 'ca'] as const).map((perspective) => {
			const isLoading = loadingKeys.has(`${userId}:${perspective}`)
			const label = PERSPECTIVE_LABELS[perspective]
			const blockedNoManager = perspective === 'manager' && !hasManager
			const tooltip = blockedNoManager ? 'No primary manager assigned for this participant' : undefined
			return (
				<Button
					key={perspective}
					size='small'
					variant='secondary'
					disabled={isLoading || blockedNoManager}
					title={tooltip}
					icon={isLoading ? undefined : PdfIcon}
					iconPosition='left'
					onClick={(e) => {
						e.stopPropagation()
						onDownload(userId, perspective)
					}}>
					{isLoading ? (
						<>
							<Spinner className='w-3 h-3 mr-1' />
							{label}
						</>
					) : (
						label
					)}
				</Button>
			)
		})}
	</div>
)

interface ManagePdfsTabProps {
	cycleId: string
}

// Types for evaluation data from getEvaluationsForDownload
interface EvaluationTask {
	id: string
	cycleStepDisplayName: string
	evaluatorId: string
	evaluatorFirstName: string | null
	evaluatorLastName: string | null
	calculatedRole: string | null
}

interface Evaluation {
	evaluationId: string
	participantId: string
	userId: string
	firstName: string
	lastName: string
	formId: string | null
	reviewerCount: number
	tasks: EvaluationTask[]
	customFields?: Record<string, string> | null
}

// Step types that have multiple evaluators
const MULTI_EVALUATOR_STEPS = ['group_evaluation', 'unsolicited_evaluation', 'manager_evaluation', 'review'] as const

export const ManagePdfsTab: React.FC<ManagePdfsTabProps> = ({ cycleId }) => {
	const { showToast } = useToast()
	const [selectedPhase, setSelectedPhase] = useState<string>('all')
	const [searchQuery, setSearchQuery] = useState<string>('')
	const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set())
	const [currentJobId, setCurrentJobId] = useState<string | null>(null)
	const [isDownloadHistoryModalOpen, setIsDownloadHistoryModalOpen] = useState(false)
	const [customFieldFilters, setCustomFieldFilters] = useState<Record<string, string>>({})
	const [singlePdfJobs, setSinglePdfJobs] = useState<Map<string, string>>(new Map()) // taskId → jobId
	// perspectiveLoading keys are `${userId}:${'self'|'manager'|'ca'}`
	const [perspectiveLoading, setPerspectiveLoading] = useState<Set<string>>(new Set())
	// Tracks in-flight aggregated PDF jobs awaiting the async worker. Same key format as perspectiveLoading.
	const [perspectivePdfJobs, setPerspectivePdfJobs] = useState<Map<string, string>>(new Map())

	// Fetch evaluations using the new endpoint
	const { data: evaluationsData, isLoading } = useQuery({
		...trpc.client.reports.getEvaluationsForDownload.queryOptions({ cycleId }),
		enabled: !!cycleId,
	})

	const evaluations = (evaluationsData?.evaluations || []) as Evaluation[]

	// Fetch available custom fields for filters
	const { data: availableCustomFields } = useQuery({
		...trpc.client.reports.getCustomFieldsForPdf.queryOptions({ cycleId }),
		enabled: !!cycleId,
	})

	// Fetch available PDFs — used to detect reviewee-only participants (no self-evaluation task)
	const { data: availablePdfs } = useQuery({
		...trpc.client.reports.listAvailablePdfs.queryOptions({ cycleId }),
		enabled: !!cycleId,
	})

	// Fetch job history
	const { data: jobHistory, refetch: refetchHistory } = useQuery({
		...trpc.client.reports.getBulkPdfJobHistory.queryOptions({ cycleId }),
		enabled: !!cycleId,
		refetchInterval: currentJobId ? 5000 : false,
	})

	// Poll for job status when we have a job ID
	const { data: jobStatus } = useQuery({
		...trpc.client.reports.getBulkPdfJobStatus.queryOptions({ jobId: currentJobId as string }),
		enabled: !!currentJobId,
		refetchInterval: 5000,
	})

	const bulkPdfMutation = useMutation({
		...trpc.client.reports.generateBulkPdf.mutationOptions(),
		onSuccess: (data) => {
			// Show confirmation toast immediately
			showToast(
				`PDF generation started for ${selectedTaskIds.size} ${selectedTaskIds.size === 1 ? 'file' : 'files'}. Check Download History when ready.`,
				'info',
			)
			// Clear selection so user can make new selections
			setSelectedTaskIds(new Set())
			// Track job ID for auto-download when complete
			setCurrentJobId(data.jobId)
			// Refresh history to show the new job
			refetchHistory()
		},
		onError: (error) => {
			showToast(`Failed to start PDF generation: ${error.message}`, 'error')
		},
	})

	// Handle job status changes
	useEffect(() => {
		if (!jobStatus || !currentJobId) return

		if (jobStatus.status === 'completed') {
			setCurrentJobId(null)

			if (jobStatus.downloadUrl) {
				downloadFile(jobStatus.downloadUrl, `bulk-pdfs-${new Date().toISOString().split('T')[0]}.zip`)

				const pdfCount = jobStatus.pdfCount ?? 0
				showToast(`Successfully generated ${pdfCount} PDF${pdfCount !== 1 ? 's' : ''}`)
			} else {
				showToast('PDF generation completed but download URL is missing', 'error')
			}
		} else if (jobStatus.status === 'failed') {
			setCurrentJobId(null)
			showToast(`PDF generation failed: ${jobStatus.errorMessage || 'Unknown error'}`, 'error')
		}
	}, [jobStatus, currentJobId, showToast])

	// Refetch history when modal opens
	useEffect(() => {
		if (isDownloadHistoryModalOpen) {
			refetchHistory()
		}
	}, [isDownloadHistoryModalOpen, refetchHistory])

	// Extract unique phases from evaluations
	const phaseOptions = useMemo(() => {
		if (!evaluations.length) return [{ value: 'all', label: 'All Phases' }]

		const phases = new Set<string>()
		evaluations.forEach((evaluation) => {
			evaluation.tasks.forEach((task) => {
				phases.add(task.cycleStepDisplayName)
			})
		})

		const options = [{ value: 'all', label: 'All Phases' }]
		Array.from(phases)
			.sort()
			.forEach((phase) => {
				options.push({ value: phase, label: formatStepName(phase) })
			})

		return options
	}, [evaluations])

	// Filter evaluations by search and phase
	const filteredEvaluations = useMemo(() => {
		let filtered = evaluations

		// Filter by search
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase()
			filtered = filtered.filter((evaluation) => {
				const fullName = `${evaluation.firstName} ${evaluation.lastName}`.toLowerCase()
				return fullName.includes(query)
			})
		}

		// Filter by phase - only show evaluations that have tasks matching the selected phase
		if (selectedPhase !== 'all') {
			filtered = filtered.filter((evaluation) =>
				evaluation.tasks.some((task) => task.cycleStepDisplayName === selectedPhase),
			)
		}

		// Filter by custom fields (Department, Title, Location, etc.)
		Object.entries(customFieldFilters).forEach(([fieldName, filterValue]) => {
			if (filterValue && filterValue !== 'all') {
				filtered = filtered.filter((evaluation) => {
					const fieldValue = evaluation.customFields?.[fieldName]
					return fieldValue === filterValue
				})
			}
		})

		// Sort by name
		filtered.sort((a, b) => {
			const nameA = `${a.lastName} ${a.firstName}`.toLowerCase()
			const nameB = `${b.lastName} ${b.firstName}`.toLowerCase()
			return nameA.localeCompare(nameB)
		})

		return filtered
	}, [evaluations, searchQuery, selectedPhase, customFieldFilters])

	// Calculate total PDF count (sum of all tasks across all evaluations)
	const pdfStats = useMemo(() => {
		const totalParticipants = evaluations.length
		const totalPdfs = evaluations.reduce((sum, e) => sum + e.tasks.length, 0)
		const filteredPdfs = filteredEvaluations.reduce(
			(sum, e) =>
				sum +
				(selectedPhase === 'all'
					? e.tasks.length
					: e.tasks.filter((t) => t.cycleStepDisplayName === selectedPhase).length),
			0,
		)
		const selectedPdfs = selectedTaskIds.size
		return { totalParticipants, totalPdfs, filteredPdfs, selectedPdfs }
	}, [evaluations, filteredEvaluations, selectedPhase, selectedTaskIds])

	// Reviewee-only = participant has no self-evaluation task AND does not appear in the main
	// evaluations list. searchEvaluations returns any participant whose evaluation has a non-stale
	// task (incl. reviewer-role tasks), so we must exclude those user IDs to avoid duplicate rows.
	// Wait until both queries have resolved — otherwise an empty `evaluationUserIds` set would
	// briefly classify every available PDF as reviewee-only and flicker the section.
	const revieweeOnlyParticipants = useMemo(() => {
		if (!availablePdfs || !evaluationsData) return []
		const evaluationUserIds = new Set(evaluations.map((e) => e.userId))
		return availablePdfs.filter(
			(p) => p.roleType === 'participant' && !p.taskId && !evaluationUserIds.has(p.participantId),
		)
	}, [availablePdfs, evaluationsData, evaluations])

	// Lookup of `hasManager` per participant userId, sourced from `listAvailablePdfs`. Drives
	// the disabled state on the Manager perspective button so the user gets a clear UI signal
	// instead of a generic post-click toast when the participant has no manager in the
	// hierarchy. Defaults to `true` while the query is still loading so we don't briefly grey
	// out the button on every row before the data arrives.
	const hasManagerByUserId = useMemo(() => {
		const map = new Map<string, boolean>()
		for (const pdf of availablePdfs ?? []) {
			if (pdf.roleType === 'participant') {
				map.set(pdf.participantId, pdf.hasManager)
			}
		}
		return map
	}, [availablePdfs])

	// Get tasks for a filtered evaluation (filtered by phase)
	const getFilteredTasks = useCallback(
		(evaluation: Evaluation) => {
			if (selectedPhase === 'all') return evaluation.tasks
			return evaluation.tasks.filter((task) => task.cycleStepDisplayName === selectedPhase)
		},
		[selectedPhase],
	)

	// Format task label (like SearchEvaluation)
	const formatTaskLabel = (task: EvaluationTask) => {
		const stepName = formatStepName(task.cycleStepDisplayName)

		const isMultiEvaluator = (MULTI_EVALUATOR_STEPS as readonly string[]).includes(
			task.cycleStepDisplayName.toLowerCase(),
		)

		if (isMultiEvaluator && task.evaluatorFirstName && task.evaluatorLastName) {
			return `${stepName} - ${task.evaluatorFirstName} ${task.evaluatorLastName}`
		}

		return stepName
	}

	const downloadTaskPdfMutation = useMutation({
		...trpc.client.reports.generateTaskPdfAsAdmin.mutationOptions(),
	})

	const handleDownload = (taskId: string) => {
		setSinglePdfJobs((prev) => new Map(prev).set(taskId, ''))
		downloadTaskPdfMutation.mutate(
			{ taskId },
			{
				onSuccess: (data) => {
					setSinglePdfJobs((prev) => {
						const next = new Map(prev)
						next.set(taskId, data.jobId)
						return next
					})
				},
				onError: (error) => {
					showToast(`Failed to start PDF generation: ${error.message}`, 'error')
					setSinglePdfJobs((prev) => {
						const next = new Map(prev)
						next.delete(taskId)
						return next
					})
				},
			},
		)
	}

	// Poll for single PDF job completion
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const downloadedJobsRef = useRef<Set<string>>(new Set())
	useEffect(() => {
		const activeJobs = Array.from(singlePdfJobs.entries()).filter(([, jobId]) => jobId !== '')
		if (activeJobs.length === 0) {
			if (pollingRef.current) {
				clearInterval(pollingRef.current)
				pollingRef.current = null
			}
			return
		}

		pollingRef.current = setInterval(async () => {
			for (const [taskId, jobId] of activeJobs) {
				if (downloadedJobsRef.current.has(jobId)) continue
				try {
					const status = await trpcClient.client.reports.getBulkPdfJobStatus.query({ jobId })
					if (status.status === 'completed' && status.downloadUrl) {
						downloadedJobsRef.current.add(jobId)
						downloadFile(status.downloadUrl, `task-pdf-${taskId.slice(-8)}.pdf`)
						setSinglePdfJobs((prev) => {
							const next = new Map(prev)
							next.delete(taskId)
							return next
						})
					} else if (status.status === 'failed') {
						downloadedJobsRef.current.add(jobId)
						showToast(`PDF generation failed: ${status.errorMessage || 'Unknown error'}`, 'error')
						setSinglePdfJobs((prev) => {
							const next = new Map(prev)
							next.delete(taskId)
							return next
						})
					}
				} catch {
					// Silently retry on next interval
				}
			}
		}, 2000)

		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current)
				pollingRef.current = null
			}
		}
	}, [singlePdfJobs, showToast])

	// Poll for aggregated (Self/Manager/CA perspective) PDF job completion
	const perspectivePollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
	useEffect(() => {
		const activeJobs = Array.from(perspectivePdfJobs.entries())
		if (activeJobs.length === 0) {
			if (perspectivePollingRef.current) {
				clearInterval(perspectivePollingRef.current)
				perspectivePollingRef.current = null
			}
			return
		}

		const clearKey = (key: string) => {
			setPerspectivePdfJobs((prev) => {
				const next = new Map(prev)
				next.delete(key)
				return next
			})
			setPerspectiveLoading((prev) => {
				const next = new Set(prev)
				next.delete(key)
				return next
			})
		}

		perspectivePollingRef.current = setInterval(async () => {
			for (const [key, jobId] of activeJobs) {
				if (downloadedJobsRef.current.has(jobId)) continue
				try {
					const status = await trpcClient.client.reports.getBulkPdfJobStatus.query({ jobId })
					if (status.status === 'completed' && status.downloadUrl) {
						downloadedJobsRef.current.add(jobId)
						// S3 key includes the server-built filename (e.g. FirstName_LastName_self_Cycle.pdf)
						const fileName = new URL(status.downloadUrl).pathname.split('/').pop() || `pdf-${jobId.slice(-8)}.pdf`
						downloadFile(status.downloadUrl, fileName)
						clearKey(key)
					} else if (status.status === 'failed') {
						downloadedJobsRef.current.add(jobId)
						showToast(`PDF generation failed: ${status.errorMessage || 'Unknown error'}`, 'error')
						clearKey(key)
					}
				} catch {
					// Silently retry on next interval
				}
			}
		}, 2000)

		return () => {
			if (perspectivePollingRef.current) {
				clearInterval(perspectivePollingRef.current)
				perspectivePollingRef.current = null
			}
		}
	}, [perspectivePdfJobs, showToast])

	const handleToggleTask = (taskId: string) => {
		setSelectedTaskIds((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(taskId)) {
				newSet.delete(taskId)
			} else {
				newSet.add(taskId)
			}
			return newSet
		})
	}

	const handleToggleEvaluation = (evaluationId: string) => {
		const evaluation = filteredEvaluations.find((e) => e.evaluationId === evaluationId)
		if (!evaluation) return

		const taskIds = getFilteredTasks(evaluation).map((t) => t.id)
		const allSelected = taskIds.every((id) => selectedTaskIds.has(id))

		setSelectedTaskIds((prev) => {
			const newSet = new Set(prev)
			if (allSelected) {
				taskIds.forEach((id) => {
					newSet.delete(id)
				})
			} else {
				taskIds.forEach((id) => {
					newSet.add(id)
				})
			}
			return newSet
		})
	}

	const handleSelectAll = () => {
		const allFilteredTaskIds = filteredEvaluations.flatMap((e) => getFilteredTasks(e).map((t) => t.id))
		const allSelected = allFilteredTaskIds.every((id) => selectedTaskIds.has(id))

		if (allSelected) {
			setSelectedTaskIds(new Set())
		} else {
			setSelectedTaskIds(new Set(allFilteredTaskIds))
		}
	}

	const handleBulkDownload = () => {
		if (selectedTaskIds.size === 0) {
			showToast('Please select at least one task')
			return
		}

		// Pass the selected task IDs directly for granular filtering
		bulkPdfMutation.mutate({
			cycleId,
			format: 'zip',
			taskIds: [...selectedTaskIds],
		})
	}

	/**
	 * Download a perspective PDF for a participant.
	 * - self:    participant view, reviewer names anonymized
	 * - manager: primary-manager view, reviewer names shown
	 * - ca:      full CA view, all components visible
	 */
	const handleDownloadPerspectivePdf = async (userId: string, perspective: 'self' | 'manager' | 'ca') => {
		const key = `${userId}:${perspective}`
		setPerspectiveLoading((prev) => new Set(prev).add(key))
		try {
			const { jobId } = await trpcClient.client.reports.generatePdfAsAdmin.mutate({
				cycleId,
				participantId: userId,
				roleType: perspective === 'manager' ? 'primary_manager' : 'participant',
				anonymizeReviewerNames: perspective === 'self',
			})
			setPerspectivePdfJobs((prev) => new Map(prev).set(key, jobId))
		} catch (error) {
			showToast(`Failed to start PDF generation: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error')
			setPerspectiveLoading((prev) => {
				const next = new Set(prev)
				next.delete(key)
				return next
			})
		}
	}

	if (!cycleId) {
		return (
			<div className='p-6'>
				<EmptyState icon={PdfIcon} title='No Cycle Selected' description='Please select a cycle to manage PDFs' />
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className='p-6 flex justify-center items-center'>
				<Spinner />
			</div>
		)
	}

	return (
		<div className='p-6 space-y-6'>
			{/* Header */}
			<div className='flex items-center justify-between'>
				<div>
					<Text variant='large' weight='semibold'>
						Manage PDFs
					</Text>
					<Text variant='small' className='text-neutral-500 mt-1'>
						Download individual or bulk PDFs for this cycle
					</Text>
				</div>
				{/* Download History Button - always show if there are jobs in flight or in history */}
				{(() => {
					// Bulk + per-task + perspective jobs all count toward the in-flight indicator,
					// but only currentJobId can drive the bulk-zip auto-download useEffect — so we
					// keep that state separate and just derive the visual state from all three.
					const hasInflightJob =
						!!currentJobId || perspectivePdfJobs.size > 0 || Array.from(singlePdfJobs.values()).some((id) => id !== '')
					if (!(jobHistory && jobHistory.length > 0) && !hasInflightJob) return null
					return (
						<Button
							size='medium'
							variant={hasInflightJob ? 'primary' : 'secondary'}
							onClick={() => setIsDownloadHistoryModalOpen(true)}
							icon={hasInflightJob ? undefined : ZipIcon}
							iconPosition='left'>
							{hasInflightJob ? (
								<>
									<Spinner className='w-4 h-4 mr-2' />
									Processing... (
									{jobHistory?.filter((j) => j.status === 'processing' || j.status === 'pending').length || 1})
								</>
							) : (
								`Download History (${jobHistory?.length || 0})`
							)}
						</Button>
					)
				})()}
			</div>

			{/* Stats Cards */}
			<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
				<div className='bg-white border border-neutral-200 rounded-lg p-4 shadow-sm'>
					<Text variant='xsmall' className='text-neutral-500 uppercase tracking-wide'>
						Participants
					</Text>
					<Text variant='h3' weight='semibold' className='text-neutral-900 mt-1'>
						{pdfStats.totalParticipants}
					</Text>
				</div>
				<div className='bg-white border border-neutral-200 rounded-lg p-4 shadow-sm'>
					<Text variant='xsmall' className='text-neutral-500 uppercase tracking-wide'>
						Total PDFs
					</Text>
					<Text variant='h3' weight='semibold' className='text-primary-600 mt-1'>
						{pdfStats.totalPdfs}
					</Text>
				</div>
				<div className='bg-white border border-neutral-200 rounded-lg p-4 shadow-sm'>
					<Text variant='xsmall' className='text-neutral-500 uppercase tracking-wide'>
						Showing
					</Text>
					<Text variant='h3' weight='semibold' className='text-neutral-700 mt-1'>
						{pdfStats.filteredPdfs}
					</Text>
				</div>
				<div className='bg-primary-50 border border-primary-200 rounded-lg p-4 shadow-sm'>
					<Text variant='xsmall' className='text-primary-700 uppercase tracking-wide'>
						Selected
					</Text>
					<Text variant='h3' weight='semibold' className='text-primary-700 mt-1'>
						{pdfStats.selectedPdfs}
					</Text>
				</div>
			</div>

			{/* Search and Filters */}
			<div className='space-y-3'>
				{/* Search Row */}
				<div className='max-w-md'>
					<Search
						placeholder='Search by participant name...'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						clearable={true}
					/>
				</div>

				{/* Filters Row - wraps on smaller screens */}
				<div className='flex flex-wrap items-end gap-3'>
					{/* Phase Filter */}
					<div className='w-[160px]'>
						<Select
							label='Phase'
							options={phaseOptions}
							value={selectedPhase}
							onChange={(value: string) => setSelectedPhase(value)}
						/>
					</div>

					{/* Custom Field Filters - only show standard fields */}
					{availableCustomFields
						?.filter((field) =>
							['Department', 'Location', 'Location Name', 'Title', 'Job Title'].some(
								(standard) => field.name.toLowerCase() === standard.toLowerCase(),
							),
						)
						.map((field) => (
							<div key={field.name} className='w-[160px]'>
								<Select
									label={field.name}
									options={[{ value: 'all', label: 'All' }, ...field.values.map((v) => ({ value: v, label: v }))]}
									value={customFieldFilters[field.name] || 'all'}
									onChange={(value: string) =>
										setCustomFieldFilters((prev) => ({
											...prev,
											[field.name]: value,
										}))
									}
								/>
							</div>
						))}

					{/* Clear Filters Button - show when any filter is active */}
					{(selectedPhase !== 'all' || Object.values(customFieldFilters).some((v) => v && v !== 'all')) && (
						<Button
							variant='tertiary'
							size='small'
							onClick={() => {
								setSelectedPhase('all')
								setCustomFieldFilters({})
							}}>
							Clear Filters
						</Button>
					)}
				</div>

				{/* Bulk Selection Controls */}
				{filteredEvaluations.length > 0 && (
					<div className='flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200'>
						<div className='flex items-center gap-3'>
							<Checkbox
								checked={selectedTaskIds.size > 0}
								isAllSelected={
									selectedTaskIds.size === pdfStats.filteredPdfs ? true : selectedTaskIds.size > 0 ? false : undefined
								}
								onChange={handleSelectAll}
							/>
							<Text variant='small' weight='semibold'>
								{selectedTaskIds.size === 0
									? 'Select PDFs'
									: `${selectedTaskIds.size} PDF${selectedTaskIds.size !== 1 ? 's' : ''} selected`}
							</Text>
						</div>
						<Button
							size='medium'
							variant='primary'
							onClick={handleBulkDownload}
							disabled={selectedTaskIds.size === 0 || bulkPdfMutation.isPending}
							icon={bulkPdfMutation.isPending ? undefined : ZipIcon}
							iconPosition='left'>
							{bulkPdfMutation.isPending ? (
								<>
									<Spinner className='w-4 h-4 mr-2' />
									Starting...
								</>
							) : (
								'Download as ZIP'
							)}
						</Button>
					</div>
				)}
			</div>

			{/* Evaluations List */}
			<div className='space-y-3'>
				{filteredEvaluations.length === 0 ? (
					<div className='py-12'>
						<EmptyState
							icon={SearchIcon}
							title='No Evaluations Found'
							description={
								searchQuery.trim()
									? `No evaluations match "${searchQuery}". Try a different search term.`
									: 'No evaluations match the selected filters.'
							}
						/>
					</div>
				) : (
					filteredEvaluations.map((evaluation) => {
						const filteredTasks = getFilteredTasks(evaluation)
						const selectedCount = filteredTasks.filter((t) => selectedTaskIds.has(t.id)).length
						const allSelected = selectedCount === filteredTasks.length && filteredTasks.length > 0
						const someSelected = selectedCount > 0 && selectedCount < filteredTasks.length
						const initials = `${evaluation.firstName.charAt(0)}${evaluation.lastName.charAt(0)}`.toUpperCase()

						return (
							<div
								key={evaluation.evaluationId}
								className={`border rounded-xl bg-white transition-all duration-200 ${allSelected ? 'ring-2 ring-primary-500 border-primary-300' : someSelected ? 'ring-1 ring-primary-300 border-primary-200' : 'border-neutral-200 hover:border-neutral-300 hover:shadow-sm'}`}>
								{/* Evaluation Header — checkbox and perspective buttons are siblings of the expand toggle to avoid nested <button> elements */}
								<div
									className={`flex items-center justify-between p-4 transition-colors ${allSelected ? 'bg-primary-50/50' : 'hover:bg-neutral-50'}`}>
									<div className='flex items-center gap-4 flex-1 min-w-0'>
										<Checkbox
											checked={allSelected}
											isAllSelected={someSelected ? false : allSelected ? true : undefined}
											onChange={() => handleToggleEvaluation(evaluation.evaluationId)}
										/>
										<button
											type='button'
											className='flex items-center gap-4 flex-1 min-w-0 text-left cursor-pointer'
											onClick={() => handleToggleEvaluation(evaluation.evaluationId)}>
											{/* Avatar */}
											<div
												className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${allSelected ? 'bg-primary-600 text-white' : 'bg-gradient-to-br from-neutral-100 to-neutral-200 text-neutral-600'}`}>
												{initials}
											</div>
											<div>
												<Text variant='medium' weight='semibold' className='text-neutral-900'>
													{evaluation.firstName} {evaluation.lastName}
												</Text>
												<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
													{selectedCount > 0
														? `${selectedCount}/${filteredTasks.length} selected`
														: `${filteredTasks.length} ${filteredTasks.length === 1 ? 'PDF' : 'PDFs'}`}
												</Text>
											</div>
										</button>
									</div>

									<PerspectiveButtons
										userId={evaluation.userId}
										loadingKeys={perspectiveLoading}
										onDownload={handleDownloadPerspectivePdf}
										hasManager={hasManagerByUserId.get(evaluation.userId) ?? true}
									/>
								</div>

								{/* Tasks List */}
								<div className='border-t border-neutral-100'>
									{filteredTasks.map((task, index) => {
										const isTaskSelected = selectedTaskIds.has(task.id)

										return (
											<button
												type='button'
												key={task.id}
												className={`flex items-center gap-4 px-4 py-3 transition-colors cursor-pointer w-full text-left ${index !== filteredTasks.length - 1 ? 'border-b border-neutral-50' : ''} ${isTaskSelected ? 'bg-primary-50/30' : 'hover:bg-neutral-50'}`}
												onClick={() => handleToggleTask(task.id)}>
												{/* Checkbox */}
												{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation wrapper, keyboard events handled by parent button */}
												{/* biome-ignore lint/a11y/noStaticElementInteractions: stopPropagation wrapper for checkbox */}
												<div onClick={(e) => e.stopPropagation()} className='flex-shrink-0'>
													<Checkbox checked={isTaskSelected} onChange={() => handleToggleTask(task.id)} />
												</div>

												{/* PDF Icon */}
												<div className='w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0'>
													<PdfIcon className='w-4 h-4 text-blue-600' />
												</div>

												{/* Task Info */}
												<div className='flex-1 min-w-0'>
													<Text variant='small' weight='semibold' className='text-neutral-800'>
														{formatTaskLabel(task)}
													</Text>
													{task.calculatedRole && (
														<Text variant='xsmall' className='text-neutral-500'>
															{formatStepName(task.calculatedRole)}
														</Text>
													)}
												</div>

												{/* Action Button */}
												<Button
													size='small'
													variant='secondary'
													disabled={singlePdfJobs.has(task.id)}
													onClick={(e) => {
														e.stopPropagation()
														handleDownload(task.id)
													}}>
													{singlePdfJobs.has(task.id) ? (
														<>
															<Spinner className='w-3 h-3 mr-1' />
															Generating...
														</>
													) : (
														'Download'
													)}
												</Button>
											</button>
										)
									})}
								</div>
							</div>
						)
					})
				)}
			</div>

			{/* Other Participants — participants with no active tasks in this cycle */}
			{revieweeOnlyParticipants.length > 0 && (
				<div className='space-y-3'>
					<div>
						<Text variant='medium' weight='semibold'>
							Other Participants
						</Text>
						<Text variant='small' className='text-neutral-500 mt-1'>
							These participants have no active tasks in this cycle. You can still generate an empty PDF structure for
							them.
						</Text>
					</div>
					<div className='space-y-2'>
						{revieweeOnlyParticipants.map((participant) => {
							const initials = participant.participantName
								.split(' ')
								.map((n) => n.charAt(0))
								.join('')
								.toUpperCase()
								.slice(0, 2)
							return (
								<div
									key={participant.participantId}
									className='flex items-center gap-4 p-4 bg-white border border-neutral-200 rounded-xl hover:border-neutral-300 hover:shadow-sm transition-all'>
									<div className='w-10 h-10 rounded-full bg-gradient-to-br from-neutral-100 to-neutral-200 flex items-center justify-center text-sm font-semibold text-neutral-600 flex-shrink-0'>
										{initials}
									</div>
									<div className='flex-1 min-w-0'>
										<Text variant='medium' weight='semibold' className='text-neutral-900'>
											{participant.participantName}
										</Text>
										<Text variant='xsmall' className='text-neutral-500'>
											No active tasks
										</Text>
									</div>
									<PerspectiveButtons
										userId={participant.participantId}
										loadingKeys={perspectiveLoading}
										onDownload={handleDownloadPerspectivePdf}
										hasManager={hasManagerByUserId.get(participant.participantId) ?? true}
									/>
								</div>
							)
						})}
					</div>
				</div>
			)}

			{/* Download History Modal */}
			<Modal
				isOpen={isDownloadHistoryModalOpen}
				onClose={() => setIsDownloadHistoryModalOpen(false)}
				title='Download History'
				className='max-w-2xl'>
				<div className='space-y-4'>
					<Text variant='small' className='text-neutral-500'>
						Your bulk PDF download requests
					</Text>
					{jobHistory && jobHistory.length > 0 ? (
						<div className='space-y-2 max-h-[60vh] overflow-y-auto'>
							{jobHistory.map((job) => (
								<div
									key={job.id}
									className='flex items-center justify-between p-4 bg-white border border-neutral-200 rounded-lg'>
									<div className='flex-1'>
										<div className='flex items-center gap-3'>
											<div>
												{job.status === 'completed' && <div className='w-2 h-2 bg-green-500 rounded-full' />}
												{job.status === 'processing' && <Spinner className='w-4 h-4' />}
												{job.status === 'pending' && <div className='w-2 h-2 bg-yellow-500 rounded-full' />}
												{job.status === 'failed' && <div className='w-2 h-2 bg-red-500 rounded-full' />}
											</div>
											<div>
												<Text variant='small' weight='semibold'>
													{job.status === 'completed' && `${job.pdfCount} PDFs Ready`}
													{job.status === 'processing' && 'Generating PDFs...'}
													{job.status === 'pending' && 'Queued for Processing'}
													{job.status === 'failed' && 'Generation Failed'}
												</Text>
												<Text variant='xsmall' className='text-neutral-500'>
													Requested {new Date(job.createdAt).toLocaleString()}
												</Text>
												{job.errorMessage && (
													<Text variant='xsmall' className='text-red-600 mt-1'>
														{job.errorMessage}
													</Text>
												)}
											</div>
										</div>
									</div>
									<div>
										{job.status === 'completed' && job.downloadUrl && (
											<Button
												size='small'
												variant='primary'
												onClick={() => {
													downloadFile(job.downloadUrl as string, 'bulk-pdfs.zip')
												}}
												icon={ZipIcon}
												iconPosition='left'>
												Download ZIP
											</Button>
										)}
										{job.status === 'processing' && (
											<Text variant='small' className='text-neutral-500'>
												Processing...
											</Text>
										)}
										{job.status === 'pending' && (
											<Text variant='small' className='text-neutral-500'>
												In queue
											</Text>
										)}
									</div>
								</div>
							))}
						</div>
					) : (
						<EmptyState
							icon={ZipIcon}
							title='No Download History'
							description="You haven't requested any bulk PDF downloads yet."
						/>
					)}
				</div>
			</Modal>
		</div>
	)
}
